import { Content, Header, Page } from "@backstage/core-components";
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from "@backstage/core-plugin-api";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import { MetricsBreadcrumbs } from "./MetricsBreadcrumbs";
import { TeamMembersManagementDialog } from "./team-members-management/TeamMembersManagementDialog";
import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

interface TeamMember {
  name: string | null;
  email: string | null;
  tribe: string | null;
  team: string | null;
  domain: string | null;
  location: string | null;
  skills: string | null;
  certifications: string | null;
  certification_count: number | string | null;
  vendorname: string | null;
}

interface CertificationGaugeData {
  total_members: number;
  certified_members: number;
  mean_percentage: number;
}

interface CoverageRow {
  label: string;
  total: number;
  certified: number;
  missing: number;
  coverage: number;
  missingMembers: string[];
}

type SortDirection = "asc" | "desc";

type SortState<TColumn extends string> = {
  column: TColumn | null;
  direction: SortDirection | null;
};

const nextSortState = <TColumn extends string>(
  current: SortState<TColumn>,
  column: TColumn,
): SortState<TColumn> => {
  if (current.column !== column) {
    return { column, direction: "asc" };
  }

  if (current.direction === "asc") {
    return { column, direction: "desc" };
  }

  return { column: null, direction: null };
};

const applySortDirection = (
  result: number,
  direction: SortDirection | null,
): number => {
  return direction === "desc" ? -result : result;
};

const useStyles = makeStyles((theme) => ({
  dashboard: {
    minHeight: "100%",
    padding: theme.spacing(3),
    backgroundColor: "#f6f8fb",
    color: "#172033",
    borderRadius: 16,
  },
  hero: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: 18,
    border: "1px solid #d9e2ef",
    background: "linear-gradient(135deg, #ffffff 0%, #eef5ff 100%)",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    marginBottom: theme.spacing(1.25),
    borderRadius: 999,
    color: "#1d4ed8",
    backgroundColor: "#eaf2ff",
    border: "1px solid #c7dcff",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  heroTitle: {
    color: "#0f172a",
    fontWeight: 800,
    fontSize: 30,
    lineHeight: 1.18,
    marginBottom: theme.spacing(1),
  },
  heroText: {
    maxWidth: 820,
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.65,
  },
  statCard: {
    height: "100%",
    borderRadius: 16,
    border: "1px solid #dce6f2",
    backgroundColor: "#ffffff",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
  },
  statLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.45,
    textTransform: "uppercase",
  },
  statValue: {
    color: "#0f172a",
    fontSize: 30,
    fontWeight: 800,
    marginTop: theme.spacing(0.75),
  },
  statSubText: {
    color: "#64748b",
    fontSize: 13,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.45,
  },
  card: {
    height: "100%",
    borderRadius: 16,
    border: "1px solid #dce6f2",
    backgroundColor: "#ffffff",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
  },
  cardContent: {
    padding: theme.spacing(2.25),
    "&:last-child": {
      paddingBottom: theme.spacing(2.25),
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.75),
    paddingBottom: theme.spacing(1.25),
    borderBottom: "1px solid #edf2f7",
  },
  titleGroup: {
    minWidth: 0,
  },
  title: {
    color: "#0f172a",
    fontWeight: 800,
    fontSize: 18,
    lineHeight: 1.25,
  },
  subtitle: {
    color: "#64748b",
    fontSize: 13,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.45,
  },
  countChip: {
    flexShrink: 0,
    height: 30,
    color: "#1e3a8a",
    fontWeight: 800,
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
  },
  tableContainer: {
    maxHeight: 480,
    overflowX: "auto",
    overflowY: "auto",
    borderRadius: 14,
    border: "1px solid #dce6f2",
    backgroundColor: "#ffffff",
    boxShadow: "none",
    // Force a light, neutral scrollbar regardless of OS / Backstage theme.
    // The default dark-theme scrollbar is jarring against this light table.
    scrollbarWidth: "thin",
    scrollbarColor: "#cbd5e1 #f1f5f9",
    "&::-webkit-scrollbar": {
      width: 10,
      height: 10,
      backgroundColor: "#f1f5f9",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f5f9",
      borderRadius: 8,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#cbd5e1",
      borderRadius: 8,
      border: "2px solid #f1f5f9",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#94a3b8",
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: "#f1f5f9",
    },
  },
  table: {
    backgroundColor: "#ffffff",
  },
  tableHeadCell: {
    top: 0,
    color: "#334155",
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.35,
    textTransform: "uppercase",
    backgroundColor: "#f1f5f9",
    borderBottom: "1px solid #cbd5e1",
    whiteSpace: "nowrap",
    padding: "13px 16px",
  },
  sortableHeadCell: {
    cursor: "pointer",
    userSelect: "none",
    transition: "background-color 0.15s ease, color 0.15s ease",
    "&:hover": {
      backgroundColor: "#e2e8f0",
      color: "#0f172a",
    },
  },
  sortHeadContent: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  sortIcon: {
    display: "inline-flex",
    width: 14,
    color: "#64748b",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1,
  },
  activeSortIcon: {
    color: "#1d4ed8",
  },
  tableCell: {
    color: "#334155",
    fontSize: 14,
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #edf2f7",
    whiteSpace: "nowrap",
    padding: "13px 16px",
    verticalAlign: "middle",
  },
  compactCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  primaryCell: {
    color: "#0f172a",
    fontWeight: 800,
  },
  mutedCell: {
    color: "#64748b",
  },
  tableRow: {
    "&:nth-of-type(even) td": {
      backgroundColor: "#fbfdff",
    },
    "&:hover td": {
      backgroundColor: "#eef6ff",
    },
  },
  certificationChip: {
    maxWidth: 150,
    color: "#166534",
    fontWeight: 800,
    backgroundColor: "#dcfce7",
    border: "1px solid #86efac",
  },
  notCertifiedChip: {
    color: "#9a3412",
    fontWeight: 800,
    backgroundColor: "#ffedd5",
    border: "1px solid #fed7aa",
  },
  certificationCell: {
    width: 280,
    minWidth: 220,
    maxWidth: 280,
    whiteSpace: "normal",
  },
  certificationText: {
    display: "-webkit-box",
    maxHeight: 42,
    overflow: "hidden",
    color: "#334155",
    lineHeight: 1.45,
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    wordBreak: "break-word",
  },
  skillCell: {
    width: 300,
    minWidth: 220,
    maxWidth: 300,
    whiteSpace: "normal",
  },
  skillText: {
    display: "-webkit-box",
    maxHeight: 42,
    overflow: "hidden",
    lineHeight: 1.45,
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    wordBreak: "break-word",
  },
  gaugeShell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(2),
  },
  gaugeTopLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  gaugeBadge: {
    height: 28,
    color: "#1d4ed8",
    fontWeight: 800,
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
  },
  gaugeSummary: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1, 0, 1.25),
  },
  gaugeRing: {
    width: 150,
    height: 150,
    minWidth: 150,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow:
      "inset 0 0 0 1px #e2e8f0, 0 10px 22px rgba(37, 99, 235, 0.12)",
  },
  gaugeInner: {
    width: 110,
    height: 110,
    borderRadius: "50%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff",
    border: "1px solid #dce6f2",
  },
  gaugeValue: {
    color: "#0f172a",
    fontSize: 30,
    fontWeight: 900,
    lineHeight: 1,
  },
  gaugeLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 800,
    marginTop: theme.spacing(0.75),
    letterSpacing: 0.45,
    textTransform: "uppercase",
  },
  gaugeHeadline: {
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1.2,
    textAlign: "center",
    marginTop: theme.spacing(1.5),
  },
  gaugeDescription: {
    maxWidth: 300,
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.55,
    marginTop: theme.spacing(0.5),
    textAlign: "center",
  },
  metricGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.25),
    borderTop: "1px solid #edf2f7",
  },
  metricBox: {
    padding: theme.spacing(1.25, 1),
    borderRadius: 12,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    textAlign: "center",
  },
  metricLabel: {
    color: "#64748b",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.35,
    textTransform: "uppercase",
  },
  metricValue: {
    color: "#0f172a",
    fontSize: 20,
    fontWeight: 900,
    marginTop: theme.spacing(0.35),
  },
  insightCard: {
    height: "100%",
    borderRadius: 16,
    border: "1px solid #dce6f2",
    backgroundColor: "#ffffff",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
  },
  miniTableContainer: {
    maxHeight: 336,
    overflowY: "auto",
    overflowX: "auto",
    borderRadius: 14,
    border: "1px solid #dce6f2",
    backgroundColor: "#ffffff",
    // Match tableContainer: keep scrollbar light/neutral in any theme.
    scrollbarWidth: "thin",
    scrollbarColor: "#cbd5e1 #f1f5f9",
    "&::-webkit-scrollbar": {
      width: 10,
      height: 10,
      backgroundColor: "#f1f5f9",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f5f9",
      borderRadius: 8,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#cbd5e1",
      borderRadius: 8,
      border: "2px solid #f1f5f9",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#94a3b8",
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: "#f1f5f9",
    },
  },
  missingInfoChip: {
    flexShrink: 0,
    color: "#92400e",
    fontWeight: 800,
    backgroundColor: "#fef3c7",
    border: "1px solid #fde68a",
    cursor: "help",
  },
  noMissingInfoChip: {
    flexShrink: 0,
    color: "#166534",
    fontWeight: 800,
    backgroundColor: "#dcfce7",
    border: "1px solid #86efac",
  },
  coverageCell: {
    minWidth: 150,
  },
  coverageBarTrack: {
    height: 8,
    overflow: "hidden",
    borderRadius: 999,
    backgroundColor: "#e2e8f0",
    marginTop: theme.spacing(0.75),
  },
  coverageBarFill: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#2563eb",
  },
  emptyState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 160,
    borderRadius: 14,
    color: "#64748b",
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
  },
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 360,
    borderRadius: 18,
    backgroundColor: "#ffffff",
    border: "1px solid #dce6f2",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
  },
  loadingText: {
    color: "#475569",
    marginTop: theme.spacing(2),
    fontWeight: 700,
  },
  errorBox: {
    padding: theme.spacing(3),
    color: "#991b1b",
    borderRadius: 16,
    backgroundColor: "#fff1f2",
    border: "1px solid #fecdd3",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
  },
}));

const toNumberOrNull = (
  value: number | string | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalize = (value: string | null | undefined): string => {
  return value ?? "";
};

const displayValue = (value: string | null | undefined): string => {
  const normalized = normalize(value).trim();
  return normalized === "" ? "—" : normalized;
};

const displayGroupValue = (value: string | null | undefined): string => {
  const normalized = normalize(value).trim();
  return normalized === "" ? "Unassigned" : normalized;
};

const compareText = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  return displayValue(a).localeCompare(displayValue(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
};

const compareNumber = (
  a: number | string | null | undefined,
  b: number | string | null | undefined,
): number => {
  const left = toNumberOrNull(a);
  const right = toNumberOrNull(b);

  if (left === null && right === null) {
    return 0;
  }

  if (left === null) {
    return 1;
  }

  if (right === null) {
    return -1;
  }

  return left - right;
};

/**
 * Match Grafana condition:
 *
 * certifications <> ''
 *
 * In SQL, NULL <> '' is not true, so NULL is not certified.
 */
const isCertified = (member: TeamMember): boolean => {
  return member.certifications !== null && member.certifications !== "";
};

const calculatePercentage = (
  certifiedMembers: number,
  totalMembers: number,
): number => {
  if (totalMembers === 0) {
    return 0;
  }

  return (certifiedMembers * 100.0) / totalMembers;
};

const buildCoverageRows = (
  rows: TeamMember[],
  getGroup: (member: TeamMember) => string | null | undefined,
): CoverageRow[] => {
  const grouped = new Map<string, TeamMember[]>();

  rows.forEach((member) => {
    const key = displayGroupValue(getGroup(member));
    grouped.set(key, [...(grouped.get(key) ?? []), member]);
  });

  return Array.from(grouped.entries())
    .map(([label, groupedRows]) => {
      const missingMembers = groupedRows
        .filter((member) => !isCertified(member))
        .map((member) => displayValue(member.name))
        .sort((a, b) => a.localeCompare(b));

      const certified = groupedRows.length - missingMembers.length;
      const total = groupedRows.length;

      return {
        label,
        total,
        certified,
        missing: missingMembers.length,
        coverage: calculatePercentage(certified, total),
        missingMembers,
      };
    })
    .sort((a, b) => {
      if (b.missing !== a.missing) {
        return b.missing - a.missing;
      }

      return a.coverage - b.coverage;
    });
};

const SortableHeaderCell = <TColumn extends string,>({
  label,
  column,
  sortState,
  onSort,
  align,
  className,
}: {
  label: string;
  column: TColumn;
  sortState: SortState<TColumn>;
  onSort: (column: TColumn) => void;
  align?: "left" | "center" | "right";
  className?: string;
}) => {
  const classes = useStyles();

  const active = sortState.column === column;
  const icon = active
    ? sortState.direction === "asc"
      ? "↑"
      : "↓"
    : "";

  return (
    <TableCell
      className={`${classes.tableHeadCell} ${classes.sortableHeadCell} ${className ?? ""
        }`}
      align={align}
      onClick={() => onSort(column)}
      role="button"
      aria-sort={
        active
          ? sortState.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <span className={classes.sortHeadContent}>
        {label}
        <span
          className={`${classes.sortIcon} ${active ? classes.activeSortIcon : ""
            }`}
        >
          {icon}
        </span>
      </span>
    </TableCell>
  );
};

const CertificationGauge = ({
  title,
  data,
}: {
  title: string;
  data: CertificationGaugeData;
}) => {
  const classes = useStyles();

  const percentage = Number.isFinite(data.mean_percentage)
    ? data.mean_percentage
    : 0;
  const progressValue = Math.max(0, Math.min(percentage, 100));

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.gaugeTopLine}>
          <Box className={classes.titleGroup}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>
              Certification coverage overview
            </Typography>
          </Box>

          <Chip
            size="small"
            label={`${percentage.toFixed(1)}%`}
            className={classes.gaugeBadge}
          />
        </Box>

        <Box className={classes.gaugeShell}>
          <Box className={classes.gaugeSummary}>
            <Box
              className={classes.gaugeRing}
              style={{
                background: `conic-gradient(#2563eb ${progressValue * 3.6
                  }deg, #e2e8f0 0deg)`,
              }}
              aria-label={`${percentage.toFixed(1)} percent certified`}
            >
              <Box className={classes.gaugeInner}>
                <Typography className={classes.gaugeValue}>
                  {percentage.toFixed(1)}%
                </Typography>
                <Typography className={classes.gaugeLabel}>
                  Certified
                </Typography>
              </Box>
            </Box>

            <Typography className={classes.gaugeHeadline}>
              {data.certified_members} of {data.total_members} members
            </Typography>
            <Typography className={classes.gaugeDescription}>
              Members currently counted as certified in this group.
            </Typography>
          </Box>

          <Box className={classes.metricGrid}>
            <Box className={classes.metricBox}>
              <Typography className={classes.metricLabel}>Certified</Typography>
              <Typography className={classes.metricValue}>
                {data.certified_members}
              </Typography>
            </Box>

            <Box className={classes.metricBox}>
              <Typography className={classes.metricLabel}>Remaining</Typography>
              <Typography className={classes.metricValue}>
                {Math.max(data.total_members - data.certified_members, 0)}
              </Typography>
            </Box>

            <Box className={classes.metricBox}>
              <Typography className={classes.metricLabel}>Total</Typography>
              <Typography className={classes.metricValue}>
                {data.total_members}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const TeamMembersTable = ({
  title,
  rows,
}: {
  title: string;
  rows: TeamMember[];
}) => {
  const classes = useStyles();

  type TeamMemberColumn =
    | "no"
    | "name"
    | "email"
    | "tribe"
    | "team"
    | "status"
    | "certifications"
    | "count"
    | "skills"
    | "location"
    | "vendor";

  const [sortState, setSortState] = useState<SortState<TeamMemberColumn>>({
    column: null,
    direction: null,
  });

  const sortedRows = useMemo(() => {
    if (!sortState.column || !sortState.direction) {
      return rows;
    }

    return [...rows].sort((a, b) => {
      let result = 0;

      switch (sortState.column) {
        case "no":
          result = rows.indexOf(a) - rows.indexOf(b);
          break;
        case "name":
          result = compareText(a.name, b.name);
          break;
        case "email":
          result = compareText(a.email, b.email);
          break;
        case "tribe":
          result = compareText(a.tribe, b.tribe);
          break;
        case "team":
          result = compareText(a.team, b.team);
          break;
        case "status":
          result = Number(isCertified(a)) - Number(isCertified(b));
          break;
        case "certifications":
          result = compareText(a.certifications, b.certifications);
          break;
        case "count":
          result = compareNumber(a.certification_count, b.certification_count);
          break;
        case "skills":
          result = compareText(a.skills, b.skills);
          break;
        case "location":
          result = compareText(a.location, b.location);
          break;
        case "vendor":
          result = compareText(a.vendorname, b.vendorname);
          break;
        default:
          result = 0;
      }

      return applySortDirection(result, sortState.direction);
    });
  }, [rows, sortState]);

  const handleSort = (column: TeamMemberColumn) => {
    setSortState((current) => nextSortState(current, column));
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.sectionHeader}>
          <Box className={classes.titleGroup}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>
              Members, skills and certification status
            </Typography>
          </Box>

          <Chip
            size="small"
            label={`${rows.length} members`}
            className={classes.countChip}
          />
        </Box>

        {rows.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">No data available</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small" stickyHeader className={classes.table}>
              <TableHead>
                <TableRow>
                  <SortableHeaderCell
                    label="No."
                    column="no"
                    sortState={sortState}
                    onSort={handleSort}
                    className={classes.compactCell}
                  />
                  <SortableHeaderCell
                    label="Name"
                    column="name"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Email"
                    column="email"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Tribe"
                    column="tribe"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Team"
                    column="team"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Status"
                    column="status"
                    sortState={sortState}
                    onSort={handleSort}
                    className={classes.compactCell}
                  />
                  <SortableHeaderCell
                    label="Certifications"
                    column="certifications"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Count"
                    column="count"
                    sortState={sortState}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeaderCell
                    label="Skills"
                    column="skills"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Location"
                    column="location"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Vendor"
                    column="vendor"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.map((row, index) => {
                  const certificationCount = toNumberOrNull(
                    row.certification_count,
                  );
                  const certified = isCertified(row);
                  const originalIndex = rows.indexOf(row);

                  return (
                    <TableRow
                      key={`${row.email ?? "email"}-${row.name ?? "name"
                        }-${index}`}
                      className={classes.tableRow}
                    >
                      <TableCell
                        className={`${classes.tableCell} ${classes.compactCell}`}
                      >
                        <span className={classes.mutedCell}>
                          {originalIndex + 1}
                        </span>
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        <span className={classes.primaryCell}>
                          {displayValue(row.name)}
                        </span>
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {displayValue(row.email)}
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {displayValue(row.tribe)}
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {displayValue(row.team)}
                      </TableCell>

                      <TableCell
                        className={`${classes.tableCell} ${classes.compactCell}`}
                      >
                        <Chip
                          size="small"
                          label={certified ? "Certified" : "Not certified"}
                          className={
                            certified
                              ? classes.certificationChip
                              : classes.notCertifiedChip
                          }
                        />
                      </TableCell>

                      <TableCell
                        className={`${classes.tableCell} ${classes.certificationCell}`}
                      >
                        <Tooltip
                          arrow
                          placement="top-start"
                          title={displayValue(row.certifications)}
                        >
                          <span className={classes.certificationText}>
                            {displayValue(row.certifications)}
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell className={classes.tableCell} align="right">
                        {certificationCount === null ? "—" : certificationCount}
                      </TableCell>

                      <TableCell
                        className={`${classes.tableCell} ${classes.skillCell}`}
                      >
                        <Tooltip
                          arrow
                          placement="top-start"
                          title={displayValue(row.skills)}
                        >
                          <span className={classes.skillText}>
                            {displayValue(row.skills)}
                          </span>
                        </Tooltip>
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {displayValue(row.location)}
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        {displayValue(row.vendorname)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

const SummaryCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: number | string;
  helper: string;
}) => {
  const classes = useStyles();

  return (
    <Card className={classes.statCard}>
      <CardContent className={classes.cardContent}>
        <Typography className={classes.statLabel}>{label}</Typography>
        <Typography className={classes.statValue}>{value}</Typography>
        <Typography className={classes.statSubText}>{helper}</Typography>
      </CardContent>
    </Card>
  );
};

const CoverageBreakdownTable = ({
  title,
  subtitle,
  rows,
}: {
  title: string;
  subtitle: string;
  rows: CoverageRow[];
}) => {
  const classes = useStyles();

  type CoverageColumn =
    | "group"
    | "members"
    | "certified"
    | "coverage"
    | "missing";

  const [sortState, setSortState] = useState<SortState<CoverageColumn>>({
    column: null,
    direction: null,
  });

  const sortedRows = useMemo(() => {
    if (!sortState.column || !sortState.direction) {
      return rows;
    }

    return [...rows].sort((a, b) => {
      let result = 0;

      switch (sortState.column) {
        case "group":
          result = a.label.localeCompare(b.label, undefined, {
            sensitivity: "base",
            numeric: true,
          });
          break;
        case "members":
          result = a.total - b.total;
          break;
        case "certified":
          result = a.certified - b.certified;
          break;
        case "coverage":
          result = a.coverage - b.coverage;
          break;
        case "missing":
          result = a.missing - b.missing;
          break;
        default:
          result = 0;
      }

      return applySortDirection(result, sortState.direction);
    });
  }, [rows, sortState]);

  const handleSort = (column: CoverageColumn) => {
    setSortState((current) => nextSortState(current, column));
  };

  return (
    <Card className={classes.insightCard}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.sectionHeader}>
          <Box className={classes.titleGroup}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>{subtitle}</Typography>
          </Box>

          <Chip
            size="small"
            label={`${rows.length} groups`}
            className={classes.countChip}
          />
        </Box>

        {rows.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">No grouped data available</Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            className={classes.miniTableContainer}
          >
            <Table size="small" stickyHeader className={classes.table}>
              <TableHead>
                <TableRow>
                  <SortableHeaderCell
                    label="Group"
                    column="group"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Members"
                    column="members"
                    sortState={sortState}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeaderCell
                    label="Certified"
                    column="certified"
                    sortState={sortState}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeaderCell
                    label="Coverage"
                    column="coverage"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                  <SortableHeaderCell
                    label="Missing"
                    column="missing"
                    sortState={sortState}
                    onSort={handleSort}
                    align="right"
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow key={row.label} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.primaryCell}>{row.label}</span>
                    </TableCell>

                    <TableCell className={classes.tableCell} align="right">
                      {row.total}
                    </TableCell>

                    <TableCell className={classes.tableCell} align="right">
                      {row.certified}
                    </TableCell>

                    <TableCell
                      className={`${classes.tableCell} ${classes.coverageCell}`}
                    >
                      <strong>{row.coverage.toFixed(1)}%</strong>
                      <Box className={classes.coverageBarTrack}>
                        <Box
                          className={classes.coverageBarFill}
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(row.coverage, 100),
                            )}%`,
                          }}
                        />
                      </Box>
                    </TableCell>

                    <TableCell className={classes.tableCell} align="right">
                      <Tooltip
                        arrow
                        placement="top-end"
                        title={
                          row.missing > 0
                            ? `Missing certification: ${row.missingMembers.join(
                              ", ",
                            )}`
                            : "No missing certifications in this group"
                        }
                      >
                        <Chip
                          size="small"
                          label={`${row.missing} member${row.missing === 1 ? "" : "s"
                            }`}
                          className={
                            row.missing > 0
                              ? classes.missingInfoChip
                              : classes.noMissingInfoChip
                          }
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

const buildLocationGauge = ({
  allMembers,
  cloudMembers,
  location,
}: {
  allMembers: TeamMember[];
  cloudMembers: TeamMember[];
  location: string;
}): CertificationGaugeData => {
  const totalMembers = allMembers.filter(
    (member) => member.location === location,
  ).length;

  const certifiedMembers = cloudMembers.filter(
    (member) => member.location === location && isCertified(member),
  ).length;

  return {
    total_members: totalMembers,
    certified_members: certifiedMembers,
    mean_percentage: calculatePercentage(certifiedMembers, totalMembers),
  };
};

const buildTribeGauge = ({
  allMembers,
  cloudMembers,
  tribe,
}: {
  allMembers: TeamMember[];
  cloudMembers: TeamMember[];
  tribe: string;
}): CertificationGaugeData => {
  const totalMembers = allMembers.filter(
    (member) => member.tribe === tribe && member.vendorname === "",
  ).length;

  const certifiedMembers = cloudMembers.filter(
    (member) => member.tribe === tribe && isCertified(member),
  ).length;

  return {
    total_members: totalMembers,
    certified_members: certifiedMembers,
    mean_percentage: calculatePercentage(certifiedMembers, totalMembers),
  };
};

export const CloudCompetenceMetricsPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>();

  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const baseUrl = await discoveryApi.getBaseUrl("metrics");

        /**
         * Important:
         * Do not pass ?domain=cloud here.
         *
         * Grafana table panels use domain='cloud',
         * but several gauge denominators do NOT filter by domain.
         * Therefore the frontend needs all teams rows.
         */
        const response = await fetchApi.fetch(`${baseUrl}/teams-members`);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch team members: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        setMembers(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [discoveryApi, fetchApi]);

  const cloudMembers = useMemo(
    () => members.filter((member) => member.domain === "cloud"),
    [members],
  );

  const certifiedCloudMembers = useMemo(
    () => cloudMembers.filter((member) => isCertified(member)),
    [cloudMembers],
  );

  const cloudCoverage = useMemo(
    () => calculatePercentage(certifiedCloudMembers.length, cloudMembers.length),
    [certifiedCloudMembers.length, cloudMembers.length],
  );

  const finlandRows = useMemo(
    () => cloudMembers.filter((member) => member.location === "finland"),
    [cloudMembers],
  );

  const indiaRows = useMemo(
    () =>
      cloudMembers.filter(
        (member) =>
          member.location === "india" || member.location === "india-externals",
      ),
    [cloudMembers],
  );

  const cepRows = useMemo(
    () => cloudMembers.filter((member) => member.tribe === "CEP"),
    [cloudMembers],
  );

  const prmRows = useMemo(
    () => cloudMembers.filter((member) => member.tribe === "PRM"),
    [cloudMembers],
  );

  const boiRows = useMemo(
    () => cloudMembers.filter((member) => member.tribe === "BOI"),
    [cloudMembers],
  );

  const osfmRows = useMemo(
    () => cloudMembers.filter((member) => member.tribe === "OSFM"),
    [cloudMembers],
  );

  const dxRows = useMemo(
    () => cloudMembers.filter((member) => member.tribe === "DX"),
    [cloudMembers],
  );

  const finlandGauge = useMemo(
    () =>
      buildLocationGauge({
        allMembers: members,
        cloudMembers,
        location: "finland",
      }),
    [members, cloudMembers],
  );

  const indiaGauge = useMemo(
    () =>
      buildLocationGauge({
        allMembers: members,
        cloudMembers,
        location: "india",
      }),
    [members, cloudMembers],
  );

  const cepGauge = useMemo(
    () =>
      buildTribeGauge({
        allMembers: members,
        cloudMembers,
        tribe: "CEP",
      }),
    [members, cloudMembers],
  );

  const prmGauge = useMemo(
    () =>
      buildTribeGauge({
        allMembers: members,
        cloudMembers,
        tribe: "PRM",
      }),
    [members, cloudMembers],
  );

  const boiGauge = useMemo(
    () =>
      buildTribeGauge({
        allMembers: members,
        cloudMembers,
        tribe: "BOI",
      }),
    [members, cloudMembers],
  );

  const osfmGauge = useMemo(
    () =>
      buildTribeGauge({
        allMembers: members,
        cloudMembers,
        tribe: "OSFM",
      }),
    [members, cloudMembers],
  );

  const dxGauge = useMemo(
    () =>
      buildTribeGauge({
        allMembers: members,
        cloudMembers,
        tribe: "DX",
      }),
    [members, cloudMembers],
  );

  const teamCoverageRows = useMemo(
    () => buildCoverageRows(cloudMembers, (member) => member.team),
    [cloudMembers],
  );

  const tribeCoverageRows = useMemo(
    () => buildCoverageRows(cloudMembers, (member) => member.tribe),
    [cloudMembers],
  );

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Digital Services SW Competence Metrics" />

        <Content>
          <Box className={classes.dashboard}>
            <Box className={classes.loadingBox}>
              <CircularProgress />
              <Typography className={classes.loadingText}>
                Loading Digital Services SW competence metrics…
              </Typography>
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Digital Services SW Competence Metrics" />

        <Content>
          <Box className={classes.dashboard}>
            <Box className={classes.errorBox}>
              <Typography variant="h6">Failed to load data</Typography>
              <Typography variant="body2">{error}</Typography>
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Content>
        <Box className={classes.dashboard}>
          <MetricsBreadcrumbs currentPage="Digital Services SW Competence Metrics" />
          <Box className={classes.hero}>
            <Typography className={classes.eyebrow}>
              Digital Services SW capability dashboard
            </Typography>
            <Typography className={classes.heroTitle}>
              Certification coverage across Digital Services SW
            </Typography>
            <Typography className={classes.heroText}>
              A consolidated view of Digital Services SW members, locations, tribes,
              skill coverage and certification progress.
              <Box style={{ marginTop: 16 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setIsManageDialogOpen(true)}
                  disabled={loading}
                >
                  Manage Members
                </Button>
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label="Digital Services SW members"
                value={cloudMembers.length}
                helper="Total members in Digital Services SW"
              />
            </Grid>

            <TeamMembersManagementDialog
              open={isManageDialogOpen}
              onClose={() => setIsManageDialogOpen(false)}
              members={members}
              onRefresh={async () => {
                setIsManageDialogOpen(false);
                try {
                  setLoading(true);
                  const baseUrl = await discoveryApi.getBaseUrl("metrics");
                  const response = await fetchApi.fetch(`${baseUrl}/teams-members`);
                  if (!response.ok) throw new Error("Failed to fetch");
                  const data = await response.json();
                  setMembers(Array.isArray(data) ? data : []);
                } catch (e) {
                  setError(e instanceof Error ? e.message : "Failed to fetch data");
                } finally {
                  setLoading(false);
                }
              }}
            />

            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label="Certified members"
                value={certifiedCloudMembers.length}
                helper="Digital Services SW members with certifications"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SummaryCard
                label="Overall coverage"
                value={`${cloudCoverage.toFixed(2)}%`}
                helper="Certified Digital Services SW member ratio"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CoverageBreakdownTable
                title="Coverage by team"
                subtitle="Teams with the largest certification gaps appear first"
                rows={teamCoverageRows}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CoverageBreakdownTable
                title="Coverage by tribe"
                subtitle="Certification coverage grouped by tribe"
                rows={tribeCoverageRows}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TeamMembersTable
                title="Digital Services SW Members - Finland"
                rows={finlandRows}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <CertificationGauge
                title="Certification report - Finland"
                data={finlandGauge}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TeamMembersTable
                title="Digital Services SW Members - India"
                rows={indiaRows}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <CertificationGauge
                title="Certification report - India"
                data={indiaGauge}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TeamMembersTable title="CEP Certifications" rows={cepRows} />
            </Grid>

            <Grid item xs={12} md={4}>
              <CertificationGauge title="CEP Certification" data={cepGauge} />
            </Grid>

            <Grid item xs={12} md={8}>
              <TeamMembersTable title="PRM Certifications" rows={prmRows} />
            </Grid>

            <Grid item xs={12} md={4}>
              <CertificationGauge title="PRM Certification" data={prmGauge} />
            </Grid>

            <Grid item xs={12} md={8}>
              <TeamMembersTable title="BOI Certifications" rows={boiRows} />
            </Grid>

            <Grid item xs={12} md={4}>
              <CertificationGauge title="BOI Certification" data={boiGauge} />
            </Grid>

            <Grid item xs={12} md={8}>
              <TeamMembersTable title="OSFM Certifications" rows={osfmRows} />
            </Grid>

            <Grid item xs={12} md={4}>
              <CertificationGauge title="OSFM Certification" data={osfmGauge} />
            </Grid>

            <Grid item xs={12} md={8}>
              <TeamMembersTable title="DX Certifications" rows={dxRows} />
            </Grid>

            <Grid item xs={12} md={4}>
              <CertificationGauge title="DX Certification" data={dxGauge} />
            </Grid>
          </Grid>
        </Box>
      </Content>
    </Page>
  );
};